import type { Handler } from '@/lib/map-click-handler/types'

import {
  handleWaterbodyBadgesClicked,
  handleFishingAreaClick,
  handleWaterAccessFeatureClick,
  handleWaterbodyClick,
  handleEventClick,
  handleCatchPolygonClick,
} from '@/lib/map-click-handler/feature-handlers/fish-handlers'
import {
  handleOfflineMapClick,
  handleMarkupClick,
  handleSearchResultClick,
} from '@/lib/map-click-handler/feature-handlers/shared-handlers'

/*
 * Map handler functions to feature classification
 * Handlers have access to 4 parameters:
 ** features: the feature array containing the handler's desired matched features
 ** event: the click event
 ** zoom: the current zoom
 ** classifedFeatures: the entire classified features array (use this if your handler needs multiple feature arrays)
 *
 * NOTE: The order in here does matter - it dictates which features take priority
 * Ex 1: Items in sharedHandlers like markups are highest priority and go first
 * Ex 2: Fishing Areas & Access Sites sit on top of Waterbodies. They should be handled first
 */

// Factory for creating markup handlers with different classification keys
const createMarkupHandler = (key: string) => ({
  key,
  handler: ({ features }: Handler) => handleMarkupClick(features),
})

const markupHandler = createMarkupHandler('markupsClicked')

const offlineMapHandler = {
  key: 'offlineMapClicked',
  handler: ({ features }: Handler) => handleOfflineMapClick(features),
}

const searchResultHandler = {
  key: 'searchResultsClicked',
  handler: ({ features }: Handler) => handleSearchResultClick(features),
}

const sharedUpperHandlers = [
  markupHandler,
  offlineMapHandler,
  searchResultHandler,
]

/*
 * Fish Handler Priority Order
 *
 * When multiple features are stacked at a map click location, handlers are executed
 * in the order below. The first matching feature will be handled and displayed to the user.
 *
 * Priority order (highest to lowest):
 *  1. Markup
 *  2. Offline Map
 *  4. Search Results
 *  5. Events
 *  6. Waterbody Badges
 *  7. Fishing Areas
 *  8. Water Access
 *  9. Catch Polygons
 *  10. Waterbodies
 */
export const queryHandlerConfig = [
  ...sharedUpperHandlers,
  {
    key: 'eventClicked',
    handler: ({ features, event }: Handler) =>
      handleEventClick(features, event),
  },
  {
    key: 'waterbodyBadgesClicked',
    handler: ({ features, classifiedFeatures, event }: Handler) =>
      handleWaterbodyBadgesClicked(
        features,
        classifiedFeatures.waterbodyClicked,
        event
      ),
  },
  {
    key: 'fishingAreaClicked',
    handler: ({ features }: Handler) => handleFishingAreaClick(features),
  },
  {
    key: 'waterAccessClicked',
    handler: ({ features }: Handler) => handleWaterAccessFeatureClick(features),
  },
  {
    key: 'catchPolygonClicked',
    handler: ({ event }: Handler) => handleCatchPolygonClick(event),
  },
  {
    key: 'waterbodyClicked',
    handler: ({ features, event }: Handler) =>
      handleWaterbodyClick(features, event),
  },
]
